import React, { useState } from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { hasModulePermission, isModuleAdmin } from "../../constants/platformModules";
import MasterSection from "../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "./useFinOptions";
import useGlContext, { fmtMoney } from "./gl/glContext";
import AccountPicker from "./gl/AccountPicker";
import { Badge, DataTable, act, hint, linkBtn } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, inputStyle, pageStyle, todayYmd, useLoad } from "../../components/ui";

// Payables: suppliers, their bills (matched to goods receipts and landed costs, or to expense
// accounts), holds, payments (transfer, cheque, post-dated cheque) and what is owed.
function useCanEdit() {
  const { access } = useModuleAccess();
  return hasModulePermission(access, "FIN", "FIN_AP", "FIN_ACCOUNTANT", "FIN_CONTROLLER");
}
const TYPES = ["OEM", "PARTS", "SERVICES", "LOGISTICS", "GOVERNMENT", "OTHER"].map((t) => ({ label: t.charAt(0) + t.slice(1).toLowerCase(), value: t }));

/* ------------------------------ suppliers ------------------------------ */

function Suppliers() {
  const { access } = useModuleAccess();
  const opts = useFinOptions(["payment-terms", "currencies", "tax-rates", "suppliers"]);
  const [section, setSection] = useState("list");
  const [supplier, setSupplier] = useState(null);
  const m = (key) => ({ load: () => finService.listMaster(key, supplier && key !== "suppliers" ? { supplier_id: supplier.id } : {}), save: (id, data) => finService.saveMaster(key, id, id ? data : key === "suppliers" ? data : { ...data, supplier_id: supplier.id }) });
  const canEdit = useCanEdit();
  const admin = isModuleAdmin(access, "FIN");
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "list", label: "Suppliers" },
          { key: "sites", label: "Sites" },
          { key: "banks", label: "Bank accounts" },
        ]}
      />
      {section !== "list" ? (
        <div className="mb-3" style={{ width: 320 }}>
          <AppDropdown id="sup-pick" value={supplier ? supplier.id : null} filter placeholder="Choose the supplier" options={(opts.suppliers || []).map((o) => ({ label: o.label, value: o.value, row: o.row }))} onChange={(e) => setSupplier((opts.suppliers || []).find((o) => o.value === e.value) ? { id: e.value } : null)} />
        </div>
      ) : null}
      {section === "list" ? (
        <MasterSection
          {...m("suppliers")}
          noun="supplier"
          canEdit={admin || canEdit}
          columns={[["Number", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => r.supplier_type.toLowerCase()], ["Currency", (r) => r.currency], ["Terms", (r) => r.payment_term_name], ["On hold", (r) => (r.on_hold ? "Yes" : "—")]]}
          fieldKeys={["name", "supplier_type", "trn", "country_code", "phone", "email", "address", "payment_term_id", "default_tax_rate_code", "on_hold"]}
          lockedOnEdit={["code"]}
          defaults={{ supplier_type: "PARTS", currency: "AED", country_code: "AE", default_tax_rate_code: "AE_STD" }}
          toForm={(r) => ({ ...r, payment_term_id: optionByCode(opts["payment-terms"], r.payment_term_code), on_hold: !!r.on_hold })}
          fields={() => [
            { key: "code", label: "Supplier number", required: true, max: 30 },
            { key: "name", label: "Name", required: true, max: 200 },
            { key: "supplier_type", label: "Type", type: "select", required: true, options: TYPES },
            { key: "trn", label: "Tax number", max: 32 },
            { key: "country_code", label: "Country (2 letters)", max: 2 },
            { key: "phone", label: "Phone", max: 30 },
            { key: "email", label: "Email", max: 150 },
            { key: "address", label: "Address", type: "textarea", max: 500 },
            { key: "currency", label: "Currency", type: "select", required: true, options: (opts.currencies || []).map((o) => ({ label: o.label, value: o.code })) },
            { key: "payment_term_id", label: "Payment terms", type: "select", options: opts["payment-terms"] || [] },
            { key: "default_tax_rate_code", label: "Default VAT (reverse charge for overseas)", type: "select", options: (opts["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code })) },
            { key: "on_hold", label: "On hold: no payments", type: "checkbox" },
          ]}
        />
      ) : null}
      {section === "sites" && supplier ? (
        <MasterSection
          key={`s-${supplier.id}`}
          {...m("supplier-sites")}
          noun="site"
          deps={[supplier.id]}
          columns={[["Site", (r) => r.site_code], ["Address", (r) => r.address], ["City", (r) => r.city], ["Primary", (r) => (r.is_primary ? "Yes" : "")]]}
          fieldKeys={["address", "city", "country_code", "is_primary"]}
          lockedOnEdit={["site_code"]}
          toForm={(r) => ({ ...r, is_primary: !!r.is_primary })}
          fields={() => [{ key: "site_code", label: "Site", required: true, max: 30 }, { key: "address", label: "Address", type: "textarea", max: 500 }, { key: "city", label: "City", max: 100 }, { key: "country_code", label: "Country", max: 2 }, { key: "is_primary", label: "Primary", type: "checkbox" }]}
        />
      ) : null}
      {section === "banks" && supplier ? (
        <MasterSection
          key={`b-${supplier.id}`}
          {...m("supplier-bank-accounts")}
          noun="bank account"
          deps={[supplier.id]}
          columns={[["Bank", (r) => r.bank_name], ["Account name", (r) => r.account_name], ["IBAN", (r) => r.iban], ["Currency", (r) => r.currency]]}
          fieldKeys={["bank_name", "account_name", "iban", "swift", "currency", "is_primary"]}
          defaults={{ currency: "AED" }}
          toForm={(r) => ({ ...r, is_primary: !!r.is_primary })}
          fields={() => [
            { key: "bank_name", label: "Bank", required: true, max: 100 },
            { key: "account_name", label: "Account name", required: true, max: 150 },
            { key: "iban", label: "IBAN", max: 34 },
            { key: "swift", label: "SWIFT", max: 11 },
            { key: "currency", label: "Currency", type: "select", required: true, options: (opts.currencies || []).map((o) => ({ label: o.label, value: o.code })) },
            { key: "is_primary", label: "Primary", type: "checkbox" },
          ]}
        />
      ) : null}
      {section !== "list" && !supplier ? <div style={hint}>Choose a supplier.</div> : null}
    </>
  );
}

/* ------------------------------ bills ------------------------------ */

function NewBill({ onHide, onDone }) {
  const gl = useGlContext();
  const opts = useFinOptions(["suppliers", "locations", "tax-rates"]);
  const [h, setH] = useState({ supplier_id: null, supplier_invoice_number: "", invoice_date: todayYmd(), location_id: null, invoice_class: "STANDARD", description: "" });
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const openMatches = useLoad(() => (h.supplier_id ? finService.listOpenMatches(h.supplier_id) : Promise.resolve({ success: true, data: [] })), [h.supplier_id]);
  const supplierRow = ((opts.suppliers || []).find((o) => o.value === h.supplier_id) || {}).row || {};
  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addMatch = (mt) => setLines([...lines, { kind: "MATCH", match_type: mt.match_type, match_id: mt.id, description: `${mt.reference}: ${mt.description}`, quantity: Number(mt.qty_open), unit_price: Number(mt.unit_cost), tax_rate_code: supplierRow.default_tax_rate_code || "AE_STD" }]);
  const addExpense = () => setLines([...lines, { kind: "EXPENSE", account: null, description: "", quantity: 1, unit_price: "", tax_rate_code: supplierRow.default_tax_rate_code || "AE_STD" }]);
  const save = async () => {
    setBusy(true);
    const res = await act(() =>
      finService.createBill({
        ...h,
        lines: lines.map((l) => ({ match_type: l.kind === "MATCH" ? l.match_type : undefined, match_id: l.kind === "MATCH" ? l.match_id : undefined, account: l.kind === "EXPENSE" ? l.account : undefined, description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price), tax_rate_code: l.tax_rate_code || null })),
      })
    );
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  const used = new Set(lines.filter((l) => l.kind === "MATCH").map((l) => `${l.match_type}:${l.match_id}`));
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title="New supplier bill"
      width={1050}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy || !lines.length}>
            Save draft
          </PrimaryButton>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Supplier" required>
          <AppDropdown id="b-sup" value={h.supplier_id} filter options={opts.suppliers || []} onChange={(e) => {
              setH({ ...h, supplier_id: e.value });
              setLines([]);
            }} />
        </Field>
        <Field label="Their invoice number" required>
          <input className="form-control" style={inputStyle} value={h.supplier_invoice_number} onChange={(e) => setH({ ...h, supplier_invoice_number: e.target.value })} />
        </Field>
        <Field label="Invoice date" required>
          <input type="date" className="form-control" style={inputStyle} value={h.invoice_date} onChange={(e) => setH({ ...h, invoice_date: e.target.value })} />
        </Field>
        <Field label="Type">
          <select className="form-select" value={h.invoice_class} onChange={(e) => setH({ ...h, invoice_class: e.target.value })}>
            <option value="STANDARD">Bill</option>
            <option value="CREDIT">Debit note (credit from the supplier)</option>
          </select>
        </Field>
        <Field label="Location">
          <AppDropdown id="b-loc" value={h.location_id} options={opts.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} placeholder="Default legal entity" />
        </Field>
      </div>
      {h.supplier_id ? (
        <div className="mb-3">
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Received, not yet billed</div>
          {(openMatches.data || []).filter((mt) => !used.has(`${mt.match_type}:${mt.id}`)).length === 0 ? <div style={hint}>Nothing waiting from this supplier.</div> : null}
          {(openMatches.data || [])
            .filter((mt) => !used.has(`${mt.match_type}:${mt.id}`))
            .map((mt) => (
              <div key={`${mt.match_type}-${mt.reference}-${mt.description}`} className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                <span>
                  {mt.match_label}: {mt.reference} · {mt.description} · {Number(mt.qty_open)} @ {fmtMoney(mt.unit_cost)}
                </span>
                <button type="button" style={linkBtn} onClick={() => addMatch(mt)}>
                  Bill this
                </button>
              </div>
            ))}
        </div>
      ) : null}
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-start mb-2" style={{ borderTop: "1px solid #F3F4F6", paddingTop: 8 }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            {l.kind === "EXPENSE" && gl.structure ? <AccountPicker id={`b-acct-${i}`} structure={gl.structure} companyValue={gl.ledger ? gl.ledger.company_value : null} value={l.account} onChange={(account) => set(i, { account })} /> : <div style={{ fontSize: 13, paddingTop: 8 }}>{l.description}</div>}
            {l.kind === "EXPENSE" ? <input className="form-control mt-1" style={inputStyle} placeholder="Description" value={l.description} onChange={(e) => set(i, { description: e.target.value })} /> : null}
          </div>
          <input type="number" className="form-control" style={{ ...inputStyle, width: 80 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 130 }} aria-label="Price" placeholder="Price" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
          <div style={{ width: 170 }}>
            <AppDropdown id={`b-tx-${i}`} value={l.tax_rate_code} options={(opts["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code }))} onChange={(e) => set(i, { tax_rate_code: e.value })} />
          </div>
          <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addExpense} disabled={!h.supplier_id}>
        Add expense line
      </button>
      <Field label="Description">
        <textarea className="form-control" rows={2} value={h.description} onChange={(e) => setH({ ...h, description: e.target.value })} />
      </Field>
      <div style={hint}>Matched lines clear "received not invoiced" at the receipt cost; a price difference posts as purchase price variance (above 5% it puts the bill on hold).</div>
    </SlidePanel>
  );
}

function BillView({ id, onHide, onChanged }) {
  const canEdit = useCanEdit();
  const { data: b, loading, error, reload } = useLoad(() => finService.getBill(id), [id]);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <SlidePanel show onHide={onHide} title={b ? `Bill ${b.bill_number}` : "Bill"} width={940}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {b ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <Badge status={b.status} />
              {canEdit && b.status === "DRAFT" ? <PrimaryButton onClick={async () => (await act(() => finService.billAction(id, "submit"))) && refresh()}>Validate and submit</PrimaryButton> : null}
              {canEdit && ["DRAFT", "PENDING_APPROVAL"].includes(b.status) ? <SecondaryButton onClick={async () => window.confirm("Cancel this bill?") && (await act(() => finService.billAction(id, "cancel"))) && refresh()}>Cancel</SecondaryButton> : null}
              {canEdit && b.status === "APPROVED" ? <SecondaryButton onClick={() => setForm({ kind: "hold" })}>Put on hold</SecondaryButton> : null}
            </div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              <b>{b.supplier_name}</b> · their invoice {b.supplier_invoice_number} · {formatDate(b.invoice_date)} · due {formatDate(b.due_date)}
              <div>
                {b.currency} {fmtMoney(b.total)} (VAT {fmtMoney(b.tax_total)}) · paid {fmtMoney(b.amount_paid)} · <b>remaining {fmtMoney(b.amount_remaining)}</b>
              </div>
            </div>
            <DataTable
              rows={b.lines}
              rowKey={(l) => l.line_no}
              minWidth={760}
              columns={[
                { label: "#", render: (l) => l.line_no },
                { label: "Line", render: (l) => <div>{l.description}<div style={hint}>{l.line_kind === "EXPENSE" ? l.account : l.line_kind.toLowerCase().replace("_", " ")}</div></div> },
                { label: "Qty", num: true, render: (l) => Number(l.quantity) },
                { label: "Amount", num: true, render: (l) => fmtMoney(l.amount) },
                { label: "VAT", num: true, render: (l) => (Number(l.tax_amount) ? `${fmtMoney(l.tax_amount)} ${l.tax_rate_code}` : "—") },
                { label: "Variance", num: true, render: (l) => (Number(l.price_variance) ? fmtMoney(l.price_variance) : "—") },
              ]}
            />
            {b.holds.length ? (
              <>
                <h6 className="mt-3">Holds</h6>
                {b.holds.map((hd) => (
                  <div key={hd.id} className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                    <span>
                      {hd.hold_type.toLowerCase()}: {hd.reason} {hd.released ? `· released (${hd.release_note})` : ""}
                    </span>
                    {!hd.released && canEdit ? (
                      <button type="button" style={linkBtn} onClick={() => setForm({ kind: "release", hold: hd })}>
                        Release
                      </button>
                    ) : null}
                  </div>
                ))}
              </>
            ) : null}
            {b.payments.length ? (
              <>
                <h6 className="mt-3">Payments</h6>
                {b.payments.map((p) => (
                  <div key={p.payment_number} style={{ fontSize: 13 }}>
                    {p.payment_number} · {formatDate(p.payment_date)} · {fmtMoney(p.amount)} · {p.status.toLowerCase()}
                  </div>
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </LoadState>
      <FormDrawer show={!!form && form.kind === "hold"} title="Put on hold" initial={{}} onHide={() => setForm(null)} onDone={refresh} onSave={(clean) => finService.billAction(id, "holds", clean)} fields={[{ key: "reason", label: "Why", type: "textarea", required: true, max: 300 }]} />
      <FormDrawer show={!!form && form.kind === "release"} title="Release hold" initial={{}} onHide={() => setForm(null)} onDone={refresh} onSave={(clean) => finService.releaseBillHold(id, form.hold.id, clean.note)} fields={[{ key: "note", label: "Why it can be paid", type: "textarea", required: true, max: 300 }]} />
    </SlidePanel>
  );
}

function Bills() {
  const canEdit = useCanEdit();
  const [f, setF] = useState({ q: "", status: "" });
  const { data, loading, error, reload } = useLoad(() => finService.listBills({ q: f.q || undefined, status: f.status || undefined }), [f.q, f.status]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {canEdit ? <PrimaryButton onClick={() => setCreating(true)}>New bill</PrimaryButton> : null}
        <div style={{ width: 180 }}>
          <AppDropdown id="bill-st" value={f.status} options={[{ label: "All", value: "" }, { label: "Draft", value: "DRAFT" }, { label: "Pending approval", value: "PENDING_APPROVAL" }, { label: "Approved", value: "APPROVED" }, { label: "Cancelled", value: "CANCELLED" }]} onChange={(e) => setF({ ...f, status: e.value })} />
        </div>
        <input className="form-control" style={{ ...inputStyle, width: 240 }} placeholder="Number or supplier" aria-label="Search bills" onKeyDown={(e) => e.key === "Enter" && setF({ ...f, q: e.target.value.trim() })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No bills" />}
          columns={[
            { label: "Bill", render: (r) => <b>{r.bill_number}</b> },
            { label: "Supplier", render: (r) => r.supplier_name },
            { label: "Their number", render: (r) => r.supplier_invoice_number },
            { label: "Date", render: (r) => formatDate(r.invoice_date) },
            { label: "Due", render: (r) => formatDate(r.due_date) },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Remaining", num: true, render: (r) => fmtMoney(r.amount_remaining) },
            { label: "Status", render: (r) => <span>{r.invoice_class === "CREDIT" ? "Debit note · " : ""}<Badge status={r.status} />{r.open_holds ? <span style={{ color: "#991B1B", fontSize: 12 }}> on hold</span> : null}</span> },
          ]}
        />
      </LoadState>
      {open ? <BillView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? (
        <NewBill
          onHide={() => setCreating(false)}
          onDone={(id) => {
            reload();
            setOpen(id);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------ payments ------------------------------ */

function NewPayment({ onHide, onDone }) {
  const opts = useFinOptions(["suppliers", "bank-accounts"]);
  const [h, setH] = useState({ supplier_id: null, bank_account_id: null, method: "BANK_TRANSFER", payment_date: todayYmd(), reference: "", cheque_number: "", cheque_date: todayYmd() });
  const [pay, setPay] = useState({});
  const bills = useLoad(() => (h.supplier_id ? finService.listBills({ supplier_id: h.supplier_id, payable: 1 }) : Promise.resolve({ success: true, data: [] })), [h.supplier_id]);
  const [busy, setBusy] = useState(false);
  const rows = bills.data || [];
  const total = Object.values(pay).reduce((s, v) => s + Number(v || 0), 0);
  const save = async () => {
    setBusy(true);
    const res = await act(() =>
      finService.createPayment({
        supplier_id: h.supplier_id,
        bank_account_id: h.bank_account_id,
        method: h.method,
        payment_date: h.payment_date,
        reference: h.reference,
        cheque: ["CHEQUE", "PDC"].includes(h.method) ? { cheque_number: h.cheque_number, cheque_date: h.cheque_date } : undefined,
        invoices: rows.filter((r) => Number(pay[r.bill_number])).map((r) => ({ invoice_id: r.id, amount: Number(pay[r.bill_number]) })),
      })
    );
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Pay a supplier" width={900} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy || !(total > 0)}>Pay {fmtMoney(total)}</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Supplier" required>
          <AppDropdown id="p-sup" value={h.supplier_id} filter options={opts.suppliers || []} onChange={(e) => {
              setH({ ...h, supplier_id: e.value });
              setPay({});
            }} />
        </Field>
        <Field label="From bank account" required>
          <AppDropdown id="p-bank" value={h.bank_account_id} options={opts["bank-accounts"] || []} onChange={(e) => setH({ ...h, bank_account_id: e.value })} />
        </Field>
        <Field label="Method">
          <select className="form-select" value={h.method} onChange={(e) => setH({ ...h, method: e.target.value })}>
            <option value="BANK_TRANSFER">Bank transfer</option>
            <option value="CHEQUE">Cheque</option>
            <option value="PDC">Post-dated cheque</option>
          </select>
        </Field>
        <Field label="Date">
          <input type="date" className="form-control" style={inputStyle} value={h.payment_date} onChange={(e) => setH({ ...h, payment_date: e.target.value })} />
        </Field>
        {["CHEQUE", "PDC"].includes(h.method) ? (
          <>
            <Field label="Cheque number" required>
              <input className="form-control" style={inputStyle} value={h.cheque_number} onChange={(e) => setH({ ...h, cheque_number: e.target.value })} />
            </Field>
            <Field label="Cheque date" required>
              <input type="date" className="form-control" style={inputStyle} value={h.cheque_date} onChange={(e) => setH({ ...h, cheque_date: e.target.value })} />
            </Field>
          </>
        ) : (
          <Field label="Transfer reference">
            <input className="form-control" style={inputStyle} value={h.reference} onChange={(e) => setH({ ...h, reference: e.target.value })} />
          </Field>
        )}
      </div>
      <DataTable
        rows={rows}
        rowKey={(r) => r.bill_number}
        minWidth={620}
        empty={<div style={hint}>{h.supplier_id ? "No approved bills to pay (bills on hold are not listed as payable until released)." : "Choose the supplier."}</div>}
        columns={[
          { label: "Bill", render: (r) => `${r.bill_number} (${r.supplier_invoice_number})` },
          { label: "Due", render: (r) => formatDate(r.due_date) },
          { label: "Remaining", num: true, render: (r) => fmtMoney(r.amount_remaining) },
          { label: "Hold", render: (r) => (r.open_holds ? "On hold" : "") },
          { label: "Pay", render: (r) => <input type="number" min={0} step="any" className="form-control form-control-sm text-end" style={{ width: 140 }} aria-label={`Pay ${r.bill_number}`} disabled={!!r.open_holds} value={pay[r.bill_number] ?? ""} placeholder={fmtMoney(r.amount_remaining)} onChange={(e) => setPay({ ...pay, [r.bill_number]: e.target.value })} onDoubleClick={() => setPay({ ...pay, [r.bill_number]: Number(r.amount_remaining) })} /> },
        ]}
      />
      <div style={hint}>Double-click an amount to pay the bill in full. Payments are approved (Approvals) before they post.</div>
    </SlidePanel>
  );
}

function Payments() {
  const canEdit = useCanEdit();
  const { data, loading, error, reload } = useLoad(() => finService.listPayments({}), []);
  const cheques = useLoad(() => finService.listIssuedCheques(), []);
  const [creating, setCreating] = useState(false);
  const [voiding, setVoiding] = useState(null);
  return (
    <>
      {canEdit ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setCreating(true)}>Pay a supplier</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No payments" />}
          columns={[
            { label: "Payment", render: (p) => <b>{p.payment_number}</b> },
            { label: "Date", render: (p) => formatDate(p.payment_date) },
            { label: "Supplier", render: (p) => p.supplier_name },
            { label: "Method", render: (p) => p.method.toLowerCase().replace("_", " ") },
            { label: "Amount", num: true, render: (p) => `${fmtMoney(p.amount)} ${p.currency}` },
            { label: "Status", render: (p) => <Badge status={p.status === "PAID" ? "POSTED" : p.status === "VOID" ? "REVERSED" : p.status} /> },
            { label: "", render: (p) => (canEdit && p.status === "PAID" ? <button type="button" style={linkBtn} onClick={() => setVoiding(p)}>Void</button> : null) },
          ]}
        />
      </LoadState>
      <h6 className="mt-4">Cheques issued</h6>
      <DataTable
        rows={cheques.data || []}
        empty={<div style={hint}>None.</div>}
        columns={[
          { label: "PDC", render: (c) => c.pdc_number },
          { label: "To", render: (c) => c.party_name },
          { label: "Cheque", render: (c) => c.cheque_number },
          { label: "Dated", render: (c) => formatDate(c.cheque_date) },
          { label: "Amount", num: true, render: (c) => `${fmtMoney(c.amount)} ${c.currency}` },
          { label: "Status", render: (c) => <Badge status={c.status === "IN_HAND" ? "OPEN" : c.status === "CLEARED" ? "COMPLETED" : c.status} /> },
          { label: "", render: (c) => (canEdit && c.status === "IN_HAND" ? <button type="button" style={linkBtn} onClick={async () => (await act(() => finService.clearIssuedCheque(c.id))) && cheques.reload()}>Presented</button> : null) },
        ]}
      />
      {creating ? (
        <NewPayment
          onHide={() => setCreating(false)}
          onDone={() => {
            reload();
            cheques.reload();
          }}
        />
      ) : null}
      <FormDrawer show={!!voiding} title={voiding ? `Void ${voiding.payment_number}` : ""} initial={{}} onHide={() => setVoiding(null)} onDone={reload} onSave={(clean) => finService.voidPayment(voiding.id, clean.reason)} fields={[{ key: "reason", label: "Why", type: "textarea", required: true, max: 200 }]} />
    </>
  );
}

function PayablesAging() {
  const { data, loading, error, reload } = useLoad(() => finService.getPayablesAging({}), []);
  const labels = { CURRENT: "Not due", "1-30": "1-30", "31-60": "31-60", "61-90": "61-90", "91-120": "91-120", "120+": "120+" };
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <DataTable
          rows={data.suppliers}
          rowKey={(s) => s.code}
          minWidth={860}
          empty={<EmptyState title="Nothing owed" />}
          columns={[{ label: "Supplier", render: (s) => s.name }, ...data.buckets.map((b) => ({ label: labels[b], num: true, render: (s) => (s[b] ? fmtMoney(s[b]) : "—") })), { label: "Total", num: true, render: (s) => <b>{fmtMoney(s.total)}</b> }]}
        />
      ) : null}
    </LoadState>
  );
}

export default function Payables() {
  const [tab, setTab] = useState("bills");
  return (
    <div style={pageStyle}>
      <PageHeader title="Payables" subtitle="Suppliers, bills, payments and what is owed" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "bills", label: "Bills" },
          { key: "payments", label: "Payments" },
          { key: "suppliers", label: "Suppliers" },
          { key: "aging", label: "Aging" },
        ]}
      />
      {tab === "bills" ? <Bills /> : null}
      {tab === "payments" ? <Payments /> : null}
      {tab === "suppliers" ? <Suppliers /> : null}
      {tab === "aging" ? <PayablesAging /> : null}
    </div>
  );
}
